/**
 * Translation Key Mapping Utility
 * 
 * This file helps map between the hierarchical keys used in the React app
 * and the flat keys used in your API.
 */

// Map hierarchical keys to your API's flat keys
const keyMappings = {
  // Home page mappings
  'home.hero.title': 'home_hero_title',
  'home.features.title': 'features_title',
  'home.features.matching.title': 'matching_title',
  'home.features.matching.description': 'matching_description',
  'home.features.verified.title': 'verified_title',
  'home.features.verified.description': 'verified_description',
  'home.features.free.title': 'free_title',
  'home.features.free.description': 'free_description',
  'home.app_highlight.title': 'app_highlight_title',
  'home.app_highlight.description': 'app_highlight_description',
  
  // Prototype features
  'home.prototype.customizable.title': 'customizable_title',
  'home.prototype.customizable.description': 'customizable_description',
  'home.prototype.social_login.title': 'social_login_title',
  'home.prototype.social_login.description': 'social_login_description',
  'home.prototype.invitations.title': 'invitations_title',
  'home.prototype.invitations.description': 'invitations_description',
  'home.prototype.chat.title': 'chat_title',
  'home.prototype.chat.description': 'chat_description',
  'home.prototype.search.title': 'search_title',
  'home.prototype.search.description': 'search_description',
  'home.prototype.friends.title': 'friends_title',
  'home.prototype.friends.description': 'friends_description',
  
  // Navigation mappings
  'navigation.home': 'nav_home',
  'navigation.about': 'nav_about',
  'navigation.faq': 'nav_faq',
  'navigation.contact': 'nav_contact',
  'navigation.get_started': 'nav_get_started',
  
  // FAQ page mappings
  'faq.help_title': 'faq_help_title',
  'faq.search_placeholder': 'faq_search_placeholder',
  'faq.title': 'faq_title',
  'faq.loading': 'faq_loading',
  'faq.no_results': 'faq_no_results',
  'faq.contact_link': 'faq_contact_link',
  'faq.support_text': 'faq_support_text',
  
  // iOS invitation page mappings
  'ios.hero_title': 'ios_hero_title',
  'ios.email_notice': 'ios_email_notice',
  'ios.email_placeholder': 'ios_email_placeholder',
  'ios.register_button': 'ios_register_button',
  'ios.success_message': 'ios_success_message',
  
  // Footer mappings
  'footer.agb': 'footer_agb',
  'footer.impressum': 'footer_impressum',
  'footer.privacy': 'footer_privacy',
  'footer.contact': 'footer_contact',
  'footer.about': 'footer_about',
  'footer.faq': 'footer_faq',
  'footer.newsletter_title': 'footer_newsletter_title',
  'footer.email_placeholder': 'footer_email_placeholder',
  'footer.subscribe_button': 'footer_subscribe_button',
  'footer.rights': 'footer_rights',
  'footer.newsletter_success': 'footer_newsletter_success',
  'footer.newsletter_error': 'footer_newsletter_error',
  'footer.modal_title': 'footer_modal_title',
  'footer.modal_close': 'footer_modal_close',
  
  // Legal page mappings
  'legal.loading': 'legal_loading',
  'legal.not_found': 'legal_not_found',
  'legal.content_fallback': 'legal_content_fallback',
  
  // About page mappings
  'about.title': 'about_title',
  'about.description': 'about_description',
  
  // Common mappings
  'common.loading': 'loading',
  'common.error': 'error',
  'common.save': 'save',
  'common.cancel': 'cancel',
  'common.close': 'close',
  'common.back': 'back',
  'common.next': 'next',
  
  // Form mappings (based on your API data)
  'form.first_name': 'first_name',
  'form.last_name': 'last_name',
  'form.email': 'email',
  'form.password': 'password',
  'form.submit': 'submit'
};

// Reverse mapping for debugging
const reverseKeyMappings = Object.fromEntries(
  Object.entries(keyMappings).map(([hierarchical, flat]) => [flat, hierarchical])
);

/**
 * Convert hierarchical key to flat API key
 * @param {string} hierarchicalKey - Key like 'home.hero.title'
 * @returns {string} - Flat key like 'home_hero_title'
 */
export const getApiKey = (hierarchicalKey) => {
  return keyMappings[hierarchicalKey] || hierarchicalKey;
};

/**
 * Convert flat API key to hierarchical key
 * @param {string} flatKey - Key like 'home_hero_title'
 * @returns {string} - Hierarchical key like 'home.hero.title'
 */
export const getHierarchicalKey = (flatKey) => {
  return reverseKeyMappings[flatKey] || flatKey;
};

/**
 * Get all mapped keys for debugging
 * @returns {Object} - All key mappings
 */
export const getAllMappings = () => {
  return { keyMappings, reverseKeyMappings };
};

/**
 * Check if a hierarchical key has a mapping
 * @param {string} hierarchicalKey 
 * @returns {boolean}
 */
export const hasMapping = (hierarchicalKey) => {
  return hierarchicalKey in keyMappings;
};

/**
 * Generate API keys list for your backend
 * This helps you know what keys to add to your API
 * @returns {Array} - List of all flat keys your API should have
 */
export const getRequiredApiKeys = () => {
  return Object.values(keyMappings);
};

export default {
  getApiKey,
  getHierarchicalKey,
  getAllMappings,
  hasMapping,
  getRequiredApiKeys
};
